import { useEffect, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Button,
  Drawer,
  FormField,
  Input,
  MoneyInput,
  StyledSelect,
  type StyledSelectOption,
} from '@jumpstone/ui-react';
import type { Deduction } from '../../data/types';
import type {
  DeductionDraft,
  SaveDeductionResult,
} from '../../data/use_data_centre';
import {
  DEDUCTION_RATE_KINDS,
  deductionRateFormStateFromRule,
  defaultDeductionRateFormState,
  isDeductionRateSectionValid,
  resolveDeductionRateDraft,
  type DeductionRateFormState,
  type DeductionRateKind,
} from './deduction_drawer_validation';

/** `payroll.createPayItem`/`updatePayItem`'s exact `name` cap (`z.string().min(1).max(200)`, `apps/api/src/trpc/routers/payroll.ts`) -- validated client-side too so a too-long name never round-trips to the generic save-failed error. Same cap as `AllowanceDrawer` -- both categories share one Zod schema. */
const MAX_NAME_LENGTH = 200;

/** Matches `AllowanceDrawer`'s own `SELECT_TRIGGER_CLASSES` -- same trigger shell used across the Data Centre's other `StyledSelect`s. */
const SELECT_TRIGGER_CLASSES =
  'nv-input flex h-9 w-full cursor-pointer items-center justify-between gap-2';

/**
 * Create/edit a deduction (#22819, rate-type/amount parity #22834). Mirrors
 * `AllowanceDrawer` one-for-one: `deduction` is null when adding a new one,
 * in which case the form starts blank and `onSave` receives `id: null` — the
 * real `payroll.createPayItem` assigns the actual id server-side, this
 * drawer never fabricates one client-side.
 *
 * `onSave` calls the real `payroll.createPayItem` (`category: 'deduction'`)
 * / `payroll.updatePayItem` — the SAME entity-level `pay_items` catalog
 * write path `AllowanceDrawer` uses, just a different category. This is a
 * genuine mutate-in-place edit model (`updatePayItem` exists and is used),
 * NOT remove+re-add: unlike the per-employee `employee_deductions` table
 * (`payroll.addDeduction`/`removeDeduction`, which has no update procedure),
 * the entity-level `pay_items` catalog this screen operates on has a real
 * `updatePayItem` mutation, so an edit is a single partial update call, same
 * as an allowance edit. See `wagepro_store_data_centre.ts`'s module docblock
 * for why `addDeduction`/`removeDeduction` are never called from this
 * screen.
 *
 * A save that fails leaves the drawer open with an inline error, mirroring
 * `AllowanceDrawer`/`settings_screen.tsx`'s `handle_save`/`save_failed`
 * pattern, rather than closing optimistically.
 *
 * ── Rate type + amount (#22834) ──────────────────────────────────────────────
 *
 * This drawer used to collect ONLY a name — every deduction it created
 * silently became `rate_type: 'fixed'` with no `rate_cents` (the
 * `manager_input` fallback) regardless of what the user intended, since there
 * was no field to say otherwise. This was the EXACT same bug #22820 already
 * fixed for `AllowanceDrawer` (this drawer was built as a straight mirror of
 * that drawer's OLD, pre-#22820, name-only shape). It now also collects a
 * rate-type selector (`deduction_drawer_validation.ts`'s `DeductionRateKind`
 * — a UI-level 4-way superset of the backend's 3-way `rate_type` enum,
 * `'manager_input'` standing in for "fixed with no recorded rate") plus, for
 * the two amount-bearing kinds, a `<MoneyInput>`/percent field.
 * `payroll.createPayItem`/`updatePayItem`'s zod input applies no `category`
 * restriction to `rate_type`/`rate_cents`/`rate_bps` (verified directly
 * against `apps/api/src/trpc/routers/payroll.ts`), so the exact same
 * selector + fields `AllowanceDrawer` offers apply unchanged here.
 *
 * No basis (pre-tax/post-tax) or type (statutory/voluntary) control. There
 * is no pre-tax deduction concept to offer: `pay_items.is_pre_tax` is read by
 * no computation and Sri Lankan APIT has no pre-tax deduction (#26642, see
 * `wagepro_store_data_centre.ts`'s module docblock). Every tenant-created
 * deduction is non-statutory by construction, so there is nothing reachable
 * to toggle from this drawer.
 *
 * ── Untouched rate section round-trips unchanged (#22834, mirrors #22820 F1/F2) ──
 *
 * `rate_touched` tracks whether the user has actually interacted with the
 * rate-type selector or the amount/percent field since the drawer opened —
 * flipped to `true` only by those two `onChange` handlers, NEVER by the
 * prefill effect that seeds `rate_form` from `deduction.rule`. A pure rename
 * (the user only edits the Name field) leaves it `false`, and `handle_save`
 * passes that through to `deduction_drawer_validation.ts`'s
 * `resolveDeductionRateDraft` / `isDeductionRateSectionValid` so the save (a)
 * sends the deduction's ORIGINAL `rate_type`/`rate_cents`/`rate_bps` triad
 * back unchanged instead of re-deriving it from the current (merely
 * prefilled) selector state, and (b) is never blocked by a prefilled amount
 * that happens to be zero/invalid by the amount-bearing-kind rule. Unlike
 * allowances (where this was a retrofit an adversarial review caught
 * post-ship), this drawer gets the fix from day one. See
 * `wagepro_store_data_centre.ts`'s module docblock, same section header, for
 * the full backend-corruption rationale this closes.
 */
export function DeductionDrawer({
  open,
  deduction,
  onClose,
  onSave,
}: {
  open: boolean;
  deduction: Deduction | null;
  onClose: () => void;
  onSave: (draft: DeductionDraft) => Promise<SaveDeductionResult>;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [name, set_name] = useState('');
  const [rate_form, set_rate_form] = useState<DeductionRateFormState>(
    defaultDeductionRateFormState(),
  );
  // #22834 -- see module docblock's "Untouched rate section round-trips
  // unchanged" section. Reset alongside `rate_form` below; flipped to `true`
  // ONLY by the rate-kind selector's and amount/percent field's own
  // `onChange` handlers further down, never by this prefill effect.
  const [rate_touched, set_rate_touched] = useState(false);
  const [saving, set_saving] = useState(false);
  const [save_failed, set_save_failed] = useState(false);

  useEffect(() => {
    set_name(deduction?.name ?? '');
    set_rate_form(
      deduction
        ? deductionRateFormStateFromRule(deduction.rule)
        : defaultDeductionRateFormState(),
    );
    set_rate_touched(false);
    set_save_failed(false);
  }, [deduction, open]);

  const trimmed = name.trim();
  const is_too_long = trimmed.length > MAX_NAME_LENGTH;
  const name_valid = trimmed.length > 0 && !is_too_long;
  const name_error =
    trimmed.length === 0
      ? t('data_centre.deduction_name_required')
      : is_too_long
        ? t('data_centre.name_too_long', { max: MAX_NAME_LENGTH })
        : undefined;

  const rate_valid = isDeductionRateSectionValid(rate_form, rate_touched);
  const is_valid = name_valid && rate_valid;

  const rate_kind_options: StyledSelectOption<DeductionRateKind>[] =
    DEDUCTION_RATE_KINDS.map((kind) => ({
      value: kind,
      label: t(`data_centre.rate_kind_${kind}`),
    }));

  async function handle_save(): Promise<void> {
    set_saving(true);
    set_save_failed(false);
    const result = await onSave({
      id: deduction?.id ?? null,
      name: trimmed,
      rate: resolveDeductionRateDraft(
        rate_form,
        rate_touched,
        deduction?.rate_snapshot,
      ),
    });
    set_saving(false);
    if (result.outcome === 'success') {
      onClose();
      return;
    }
    set_save_failed(true);
  }

  const rate_amount_invalid =
    !rate_valid &&
    (rate_form.rateKind === 'fixed' || rate_form.rateKind === 'per_hour');
  const rate_percent_invalid =
    !rate_valid && rate_form.rateKind === 'percentage';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        deduction === null
          ? t('data_centre.add_deduction')
          : t('data_centre.edit_deduction')
      }
    >
      <div className="flex flex-col gap-3">
        <FormField
          label={t('data_centre.deduction_name')}
          htmlFor="deduction-name"
          error={name_error}
        >
          <Input
            id="deduction-name"
            value={name}
            onChange={(event) => {
              set_name(event.target.value);
            }}
          />
        </FormField>

        <FormField label={t('data_centre.rate_type_label')}>
          <StyledSelect
            value={rate_form.rateKind}
            onChange={(kind) => {
              set_rate_form((current) => ({ ...current, rateKind: kind }));
              set_rate_touched(true);
            }}
            options={rate_kind_options}
            ariaLabel={t('data_centre.rate_type_label')}
            triggerClassName={SELECT_TRIGGER_CLASSES}
          />
        </FormField>

        {(rate_form.rateKind === 'fixed' ||
          rate_form.rateKind === 'per_hour') && (
          <FormField
            label={t(
              rate_form.rateKind === 'fixed'
                ? 'data_centre.deduction_amount_label_fixed'
                : 'data_centre.deduction_amount_label_per_hour',
            )}
            htmlFor="deduction-amount"
            error={
              rate_amount_invalid
                ? t('data_centre.deduction_amount_required')
                : undefined
            }
          >
            <MoneyInput
              id="deduction-amount"
              value={rate_form.amountCents}
              blankZero
              onChange={(cents) => {
                set_rate_form((current) => ({
                  ...current,
                  amountCents: cents,
                }));
                set_rate_touched(true);
              }}
            />
          </FormField>
        )}

        {rate_form.rateKind === 'percentage' && (
          <FormField
            label={t('data_centre.deduction_rate_percent_label')}
            htmlFor="deduction-rate-percent"
            error={
              rate_percent_invalid
                ? t('data_centre.deduction_rate_invalid')
                : undefined
            }
          >
            <Input
              id="deduction-rate-percent"
              type="text"
              inputMode="decimal"
              value={rate_form.ratePercentText}
              onChange={(event) => {
                const value = event.target.value;
                set_rate_form((current) => ({
                  ...current,
                  ratePercentText: value,
                }));
                set_rate_touched(true);
              }}
            />
          </FormField>
        )}

        {save_failed && (
          <p className="nv-help is-error" role="alert">
            {t('data_centre.save_deduction_error')}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <Button
            variant="primary"
            size="sm"
            disabled={!is_valid || saving}
            loading={saving}
            onClick={() => void handle_save()}
          >
            {t('data_centre.save_deduction')}
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
